import React, { Component } from 'react';
import { Button, Checkbox, Paper, Typography } from '@material-ui/core';
import { RootStore } from 'store/root.store';
import AppStore from 'store/app.store';
import { inject, observer } from 'mobx-react';
import { FlexDiv } from 'common/flex';
import { Platform } from '../../util/Platform';
import logger from '../../logging/logging';
import { Settings } from 'luxon';
import { withTranslation } from 'react-i18next';
import { i18n } from 'i18next';
import { locales } from '../../i18n/config';
const constants = require('../../constants.json');

interface Props {
    rootStore?: RootStore;
    appStore?: AppStore;
    // tslint:disable-next-line:no-any
    t: any;
    i18n: i18n;
    formFieldRef: React.RefObject<HTMLElement>;
}

interface State {
    shiftKeyPressed: boolean;
    logging: boolean;
}

const AI_NARRATIVE_REWRITE_KEY = constants.aiNarrativeRewrite.settingKey;
@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        rootStore: rootStore,
        appStore: rootStore.appStore
    };
})

@observer
class GeneralSettings extends Component<Props, State> {
    state: State = {
        shiftKeyPressed: false,
        logging: false
    }
    epochDBFileRef = React.createRef<HTMLInputElement>();
    
    componentDidMount(): void {
        document.addEventListener('keyup', this.keyup);
        document.addEventListener('keydown', this.keydown);
        const enableLogging = localStorage.getItem('enableLogging');
        if (enableLogging) {
            this.setState({ logging: JSON.parse(enableLogging) });
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener('keyup', this.keyup);
        document.removeEventListener('keydown', this.keydown);
    }
    
    exportLocalDB = async () => {
        if (this.props.rootStore!.api.Session.exportLocalDBToJSON) {
            this.props.rootStore!.api.Session.exportLocalDBToJSON!();
            this.setState({ shiftKeyPressed: false });
        }
    }

    doUploadDB = () => {
        this.epochDBFileRef.current!.click();
    }

    handleDBUpload = async (selectorFiles: FileList) => {
        let file: File = selectorFiles[0];
        if (file) {
            await this.props.rootStore!.api.Session.importDB!(file);
        } else {
            logger.error('failed importing db, could not get file from input');
        }
    }

    render() {
        const { t } = this.props;
        const disableAnimsValue = this.props.appStore!.disableAnimations;
        const aiNarrativeRewrite = this.props.appStore!.disableAiNarrativeRewrite;
        const lngs: string[] = Object.keys(this.props.i18n.services.resourceStore.data);

        return (
            <Paper style={{ padding: '15px' }}>
                <FlexDiv style={{ width: '100%' }} direction="column">
                    <Typography variant="headline">
                        {t('tab.general.title')}
                    </Typography>
                    {false && // TODO: all code related to Animation needs to be removed from all files.
                    <FlexDiv direction={'row'} style={{alignItems: 'center'}}>
                        <Checkbox
                            onChange={(evt, checked) => this.setAnimationsValue(checked)}
                            checked={disableAnimsValue}
                            inputProps={{
                                'aria-label': t('tab.general.field.checkbox.animation')
                             }}
                        />
                        <Typography variant={'subheading'}>{t('tab.general.field.checkbox.animation')}</Typography>
                    </FlexDiv>}
                    <FlexDiv direction={'row'} style={{alignItems: 'center'}}>
                        <Checkbox
                            inputRef={this.props.formFieldRef}
                            onChange={(evt, checked) => this.setPassiveLogging(checked)}
                            checked={this.state.logging}
                            inputProps={{
                                'aria-label': t('tab.general.field.checkbox.logging.label')
                             }}
                        />
                        <Typography variant={'subheading'}>{t('tab.general.field.checkbox.logging.label')}</Typography>
                    </FlexDiv>
                    {this.props.appStore!.features.EpochConfigAINarrativeRewrite &&
                        <FlexDiv direction={'row'} style={{ alignItems: 'center' }}>
                            <Checkbox
                                onChange={(evt, checked) => this.setAiNarrativeRewrite(checked)}
                                checked={aiNarrativeRewrite}
                                inputProps={{
                                    'aria-label': t('tab.general.field.checkbox.narrative_rewrite.label')
                                }}
                            />
                            <Typography variant={'subheading'}>{t('tab.general.field.checkbox.narrative_rewrite.label')}</Typography>
                        </FlexDiv>
                    }
                    <FlexDiv direction={'row'} style={{alignItems: 'center'}}>
                        <Typography variant={'subheading'} style={{padding: '0 17px'}}>{t('tab.general.field.language.label')}</Typography>
                        <select
                            style={{padding: '10px'}}
                            value={this.props.i18n.language}
                            onChange={(e) => {
                                const lang = e.target.value;
                                this.props.i18n.changeLanguage(lang, () => {
                                    Settings.defaultLocale = lang;
                                });
                            }}
                        >
                            {lngs.map((lng: string) => (
                                <option value={lng} key={lng}>
                                    {locales.find(l => l.code === lng)!.name}
                                </option>
                            ))}
                        </select>
                    </FlexDiv>

                    {this.state.shiftKeyPressed && Platform.isElectron() &&
                        <FlexDiv direction={'column'} style={{alignItems: 'flex-start'}}>
                            <Button onClick={this.exportLocalDB}>
                                {t('tab.general.action.desktop.database.download')}
                            </Button>
                        </FlexDiv>
                    }
                    {Platform.isElectron() && process.env.NODE_ENV === 'development' &&
                        <FlexDiv direction={'column'} style={{alignItems: 'flex-start'}}>
                            <input
                                ref={this.epochDBFileRef}
                                style={{ display: 'none' }}
                                accept=".zip"
                                type="file"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    this.handleDBUpload(event.target.files!);
                                }}
                            />
                            <Button onClick={this.doUploadDB}>
                                {t('tab.general.action.desktop.database.upload')}
                            </Button>
                        </FlexDiv>
                    }
                </FlexDiv>
            </Paper>
        );
    }

    private setPassiveLogging = async (val: boolean) => {
        let logging = val;
        if (val) {
            this.props.rootStore!.appStore.setPreviousActiveElement();
            logging = await this.props.rootStore!.confirmStore.confirm(
                'tab.general.field.checkbox.logging.dialog.message',
                { ns: 'settings' }
            );
        }
        this.setState({ logging });
        if (logging) {
            logger.silent = false;
        } else {
            logger.silent = true;
        }
        localStorage.setItem('enableLogging', JSON.stringify(logging));
    }

    private setAiNarrativeRewrite = async (aiNarrativeRewrite: boolean) => {
        if (aiNarrativeRewrite) {
            const confirmed = await this.props.rootStore!.confirmStore.confirm(
                'tab.general.field.checkbox.narrative_rewrite.dialog.message',
                { ns: 'settings' }
            );
            if (!confirmed) { return; }
        }

        try {
          const existing = await this.props.rootStore!.api.Settings.getByKey(AI_NARRATIVE_REWRITE_KEY);
          const settingPayload = {
            ...(existing && existing.id ? { id: existing.id } : {}),
            key: AI_NARRATIVE_REWRITE_KEY,
            value: (!aiNarrativeRewrite).toString(),
            deleted: false,
            global: false
          };

          await this.props.rootStore!.api.Settings.save(settingPayload);
          this.props.appStore!.setDisableAiNarrativeRewrite(aiNarrativeRewrite);
        } catch (err) {
          logger.error('Failed to save narrative rewrite setting:', err);
        }
    };

    private setAnimationsValue(checked: boolean) {
        const store = this.props.appStore!;
        store.setDisableAnimations(checked);
    }

    private keydown = (e: KeyboardEvent) => {
        if (e.shiftKey) {
            this.setState({ shiftKeyPressed: true });
        }
    }

    private keyup = (e: KeyboardEvent) => {
        // `e.ctrlKey` for keyup events is false for some reason
        // that's why we skip the check

        if (e.keyCode === 16) {
            this.setState({ shiftKeyPressed: false });
        }
    }
}

export default withTranslation(['settings'])(GeneralSettings);